import _ from 'lodash';
import axios from 'axios';
import { BehaviorSubject } from 'rxjs';
import { Helpers } from '@/common/helpers';
import { StateService } from '@/services/state.service';

export const StoresLoadedObservable$ = new BehaviorSubject(false);

export const MemberState = {
	UNAUTHENTICATED: 'UNAUTHENTICATED',
	AUTHENTICATED: 'AUTHENTICATED',
	REMEMBERED: 'REMEMBERED'
};

let storesData = {};

let stores = {
	ClubStore: window.ef.clubStore,
	ClubsStore: window.ef.clubsStore,
	ProfileStore: window.ef.profileStore,
	ProspectProfileStore: window.ef.prospectProfileStore,
	UrlStore: window.ef.urlStore
};

function isExperience(exp) {
	let expType = window.lt.pageProperties.experienceType;
	if (Helpers.isDev()) expType = StateService.getDevSettings().experience;
	return expType === exp;
}

function handleCancelUrl(currentUrl, cancelUrl) {
	return currentUrl + (currentUrl.indexOf('?') >= 0 ? '&cancelUrl=' : '?cancelUrl=') + cancelUrl;
}

export const FrameworkService = {
	isProspectExperience: isExperience.bind(this, 'prospect'),
	isMemberExperience: isExperience.bind(this, 'member'),
	isHealthExperience: isExperience.bind(this, 'lthealth'),
	isWorkExperience: isExperience.bind(this, 'work'),
	APIM_ROOT: window.lt.api.apimUrl,
	APIM_KEY: window.lt.api.apimKey,
	maintainAppMode(url) {
		if (Helpers.isMobileApp() && !url.includes('.app.html')) {
			url = url.replace('.html', '.app.html');
		}
		return url;
	},
	data(key) {
		if (!key) {
			return storesData;
		}
		return _.get(storesData, key, null);
	},
	allClubs() {
		return storesData.clubs;
	},
	clubByName(name) {
		return _.find(storesData.clubs, item => {
			return item.name === name;
		});
	},
	globalUrl(key) {
		return this.maintainAppMode(this.data(`url.global.${key}`));
	},
	cartUrl(key) {
		return this.maintainAppMode(this.data(`url.cart.${key}`));
	},
	accountUrl(key) {
		return this.maintainAppMode(this.data(`url.account.${key}`));
	},
	clubUrl(key) {
		return this.maintainAppMode(this.data(`club.pages.${key}`));
	},
	classSearchFavoriteUrl(searchName) {
		return `${this.clubUrl('classes')}?savedSearch=${encodeURIComponent(searchName)}`;
	},
	classUrl(key) {
		var url = this.clubUrl('classDetails');
		if (url) {
			url += '?eventId=' + window.encodeURIComponent(key);
		}
		return url;
	},
	eventUrl(key) {
		var url = this.clubUrl('eventDetails');
		if (url) {
			url += '?eventId=' + window.encodeURIComponent(key);
		}
		return url;
	},
	reserveUrl(key) {
		var url = this.globalUrl('reservations');

		if (url) {
			url += '?eventId=' + window.encodeURIComponent(key);
		}
		return url;
	},
	reserveResourceUrl(resourceId, activityIds, start, duration) {
		var url = this.globalUrl('reservations');

		if (url) {
			url += '?resourceId=' + resourceId;
		}

		if (activityIds) {
			url += '&activityIds=' + activityIds;
		}

		if (start) {
			url += '&start=' + start;
		}

		if (duration) {
			url += '&duration=' + duration;
		}

		return url;
	},
	addResourceCancelUrl(url, additionalQueryString) {
		// Base64 encode querystring since cancel url has its own querystring params.
		let cancelUrl = window.location.href;
		if (additionalQueryString && !window.location.href.includes(additionalQueryString)) {
			cancelUrl += additionalQueryString;
		}

		return handleCancelUrl(this.maintainAppMode(url), window.btoa(cancelUrl));
	},
	addCancelUrl(url) {
		return handleCancelUrl(this.maintainAppMode(url), encodeURIComponent(window.location.href));
	},
	updateSavedSearches(searches) {
		if (Helpers.isDevOrUi()) {
			return;
		}
		const _update = JSON.parse(JSON.stringify(searches));
		stores.ProfileStore.updateSchregSavedSearches(
			'SchregSavedSearches',
			_update,
			function() { },
			function() { }
		);
	},
	classesUrl() {
		return '/clubs.go/classes';
	},
	facilitiesUrl() {
		return '/clubs.go/facilities';
	},
	courtsUrl() {
		return '/clubs.go/courts';
	},
	getJwe() {
		if (Helpers.isDevOrUi()) {
			return '';
		}
		return window.ef.sessionService.getCookie('lt-authentication');
	}
};

export const FrameworkServiceLoader = {
	initialize(initialDataLoaded) {
		function storeLoaded(storeName) {
			_.pull(initialDataLoaded, storeName);
			if (!initialDataLoaded.length) {
				StoresLoadedObservable$.next(true);
			}
		}

		function frameworkStore(storeName, storeMapper, storeValidator) {
			let initialized = false;
			stores[storeName].listen(onUpdateStore);
			function onUpdateStore(obj) {
				if (!obj || _.isEmpty(obj)) {
					return;
				}

				_.extend(storesData, storeMapper(obj));

				if (!initialized && storeValidator(obj)) {
					initialized = true;
					storeLoaded(storeName);
				}
			}

			if (Helpers.isDevOrUi()) {
				onUpdateStore({});
			} else {
				onUpdateStore(stores[storeName].getInitialData());
			}

			return function() {
				if (Helpers.isDevOrUi()) {
					axios.get('/mock-api/' + storeName.toLowerCase()).then((xhr) => {
						stores[storeName].simulateChange(xhr.data);
					});
				}
			};
		}

		if (initialDataLoaded.includes('ClubStore')) {
			(frameworkStore(
				'ClubStore',
				(obj) => {
					if (!obj.club) {
						return { club: {} };
					}
					return {
						club: {
							id: parseInt(obj.club.id, 10),
							name: obj.club.name,
							state: obj.club.state,
							stateName: obj.club.stateName,
							membershipLevel: obj.club.membershipLevel,
							isClubPage: obj.club.isClubPage,
							pages: obj.club.pages,
							latitude: obj.club.latitude,
							longitude: obj.club.longitude,
							updated: obj.club.updated,
							openDate: obj.club.openDate,
							phone: obj.club.phone
						}
					};
				},
				(obj) => {return obj.club.id;}
			))();
		}

		if (initialDataLoaded.includes('ClubsStore')) {
			(frameworkStore(
				'ClubsStore',
				(obj) => {
					return {
						clubs: _.map(obj.clubs, function(c) {
							return {
								id: c.mmsClubId,
								name: c.marketingClubName,
								state: c.state,
								country: c.country,
								membershipLevel: c.membershipLevel,
								latitude: c.latitude,
								longitude: c.longitude,
								status: c.open,
								openDate: c.openDate,
								phone: c.phone,
								comingSoon: c.comingSoon
							};
						})
					};
				},
				(obj) => {return obj.clubs && obj.clubs.length > 0;}
			))();
		}

		if (initialDataLoaded.includes('ProfileStore')) {
			(frameworkStore(
				'ProfileStore',
				(obj) => {
					if (Helpers.isDevOrUi() && !StateService.isMockData()) {
						const localStore = StateService.getLocalProfileStore();
						if (!storesData.profile && localStore) obj = localStore;
					}

					return {
						profile: {
							jwe: '',
							authToken: obj.authToken,
							sso: obj.sso,
							token: obj.jwt ? obj.jwt : obj.token,  // The jwt might not be present in the profile store.  In that case use what is set on token.
							partyId: obj.partyId,
							memberId: obj.memberDetails ? obj.memberDetails.memberId : 0,
							memberType: obj.memberDetails ? obj.memberDetails.memberType : '',
							membershipLevel: obj.memberDetails ? obj.memberDetails.membershipLevel : '',
							membershipLevelGroupName: obj.memberDetails ? obj.memberDetails.membershipLevelGroupName : '',
							availableClubs: obj.memberDetails ? obj.memberDetails.availableClubs : [],
							members: obj.memberDetails ? obj.memberDetails.members : '',
							updated: obj.updated,
							personalization: obj.personalization,
							memberState: obj.memberState
						}
					};
				},
				(obj) => {return obj.memberState && obj.memberState !== 'UNKNOWN' && obj.initialized;}
			))();
		}

		if (initialDataLoaded.includes('UrlStore')) {
			(frameworkStore(
				'UrlStore',
				(obj) => {
					return {
						url: {
							global: obj.globalPageDrivers ? obj.globalPageDrivers.global : {},
							account: obj.globalPageDrivers? obj.globalPageDrivers.account : {},
							cart: obj.globalPageDrivers ? obj.globalPageDrivers.cart : {}
						}
					};
				},
				(obj) => {return obj.globalPageDrivers && obj.globalPageDrivers.global.faqSchedules;}
			))();
		}

		if (initialDataLoaded.includes('ProspectProfileStore')) {
			(frameworkStore(
				'ProspectProfileStore',
				(obj) => {
					if (obj.initialized) {
						return {
							prospect: {
								isClubPage: obj.club ? obj.club.isClubPage : false,
								clubName: obj.club ? obj.club.name : null,
								clubId: obj.club ? obj.club.id : 0
							}
						};
					}
					return {
						prospect: {
							isClubPage: false,
							clubName: null,
							clubId: 0
						}
					};
				},
				(obj) => {return obj && obj.initialized;}
			))();
		}
	}
};

